// TESSERAX — ÉCHELLE DE DÉFIS (ladder curé).
//
// Les modes existants (cf. core/modes) sont tous « va le plus loin / le plus vite » :
// aucune progression AUTORALE, aucun objectif conçu à la main pour mettre en scène
// une mécanique précise. L'échelle de défis ajoute une suite de tableaux courts, à
// graine FIXE (déterministe, partageable) et à OBJECTIF unique, ordonnés par difficulté
// croissante : chaque palier isole et enseigne un feat du puits 3D (lignes → étages →
// Croix → cascade → continuum → perfect → endurance → maîtrise). On débloque le palier
// suivant en validant le précédent → une raison de revenir qui n'est pas « bats ton
// propre nombre ».
//
// CHOIX D'ARCHITECTURE : un défi n'est qu'une GRAINE + un MODE de base (gravité/feel) +
// un OBJECTIF. On réutilise tout le pipeline de run de main.ts (mulberry32(seed),
// terminaison à l'objectif, carte de fin). La complétion est persistée dans la méta
// (cf. core/meta.completedChallenges). Module PUR : ni THREE ni DOM ni Math.random.

import type { ModeId } from './modes';

// Métriques d'objectif possibles. Toutes sont déjà suivies par main.ts au cours d'une
// run (lignes, score, Croix, chaîne, continuum back-to-back, perfects, étages pleins).
export type ChallengeGoalKind = 'lines' | 'score' | 'cross' | 'chain' | 'continuum' | 'perfect' | 'slabs' | 'bascule' | 'tessera' | 'zoneLines';

export interface ChallengeGoal { kind: ChallengeGoalKind; n: number; }

export interface Challenge {
  id: string;          // stable (clé i18n challenge.<id>.* + clé de complétion méta)
  name: string;        // FR (repli) — EN via i18n
  text: string;        // une ligne : l'objectif décrit
  seed: number;        // graine de run AUTORALE (déterministe via mulberry32)
  base: ModeId;        // mode de base pour gravité/feel (endless typiquement)
  levelOffset: number; // décalage de niveau au départ (gravité initiale)
  goal: ChallengeGoal;
}

// Le ladder : ordre = difficulté croissante. Graines fixes (mémorables) → un défi
// rejoue à l'identique pour tout le monde. Chaque palier met en scène UN feat.
export const CHALLENGES: Challenge[] = [
  { id: 'bascule_school', name: 'École de Bascule', text: 'Efface 6 lignes et joue au moins une Bascule utile.',
    seed: 10_010_001, base: 'free', levelOffset: 0, goal: { kind: 'bascule', n: 1 } },
  { id: 'eveil', name: 'Éveil', text: 'Efface 8 lignes.',
    seed: 11_111_111, base: 'free', levelOffset: 0, goal: { kind: 'lines', n: 8 } },
  { id: 'tranche', name: 'La Tranche', text: 'Efface 14 lignes d’un bon rythme.',
    seed: 22_220_007, base: 'free', levelOffset: 0, goal: { kind: 'lines', n: 14 } },
  { id: 'croix', name: 'La Croix', text: 'Réussis une Croix (deux axes effacés d’un coup).',
    seed: 30_300_303, base: 'free', levelOffset: 1, goal: { kind: 'cross', n: 1 } },
  { id: 'cascade', name: 'Cascade', text: 'Déclenche une cascade de chaîne ≥ 3.',
    seed: 44_404_404, base: 'free', levelOffset: 1, goal: { kind: 'chain', n: 3 } },
  { id: 'tessera', name: 'Premier TESSERAX', text: 'Réussis un TESSERAX grâce aux deux chambres.',
    seed: 45_404_404, base: 'free', levelOffset: 1, goal: { kind: 'tessera', n: 1 } },
  { id: 'zone_bank', name: 'Banque de Zone', text: 'Atteins 25 000 points en rentabilisant la Zone.',
    seed: 46_404_404, base: 'free', levelOffset: 1, goal: { kind: 'zoneLines', n: 6 } },
  { id: 'continuum', name: 'Continuum', text: 'Enchaîne un back-to-back de maîtrise ≥ 4.',
    seed: 50_500_500, base: 'free', levelOffset: 2, goal: { kind: 'continuum', n: 4 } },
  { id: 'vide', name: 'La Singularité', text: 'Déclenche une cascade de chaîne ≥ 5.',
    seed: 60_660_066, base: 'free', levelOffset: 1, goal: { kind: 'chain', n: 5 } },
  { id: 'tempete', name: 'Tempête', text: 'Efface 30 lignes — gravité accélérée.',
    seed: 77_700_777, base: 'free', levelOffset: 3, goal: { kind: 'lines', n: 30 } },
  { id: 'seuil', name: 'Le Seuil', text: 'Atteins 60 000 points.',
    seed: 88_808_880, base: 'free', levelOffset: 2, goal: { kind: 'score', n: 60_000 } },
];

export function challengeById(id: string): Challenge | undefined { return CHALLENGES.find(c => c.id === id); }
export function challengeIndex(id: string): number { return CHALLENGES.findIndex(c => c.id === id); }

// Avancement courant d'une run vis-à-vis d'un objectif. main.ts remplit cet état avec
// ses compteurs de run existants ; on n'en lit que la métrique concernée.
export interface ChallengeProgress {
  lines: number; score: number; cross: number; chain: number; continuum: number; perfects: number; slabs: number;
  bascules?: number; tesseras?: number; zoneLines?: number;
}

function metricValue(goal: ChallengeGoal, p: ChallengeProgress): number {
  switch (goal.kind) {
    case 'lines': return p.lines;
    case 'score': return p.score;
    case 'bascule': return p.bascules || 0;
    case 'tessera': return p.tesseras || 0;
    case 'zoneLines': return p.zoneLines || 0;
    case 'cross': return p.cross;
    case 'chain': return p.chain;
    case 'continuum': return p.continuum;
    case 'perfect': return p.perfects;
    case 'slabs': return p.slabs;
    default: return 0;
  }
}

// L'objectif est-il atteint ? (≥ seuil sur la métrique concernée.)
export function challengeMet(goal: ChallengeGoal, p: ChallengeProgress): boolean {
  return metricValue(goal, p) >= goal.n;
}

// Progression pour le HUD : ratio borné 0..1 + libellé « courant / cible » locale-neutre
// (main.ts l'habille). Réutilise le gabarit de modeProgress (compte à rebours / jauge).
export interface ChallengeHud { ratio: number; label: string; }
export function challengeProgress(goal: ChallengeGoal, p: ChallengeProgress): ChallengeHud {
  const v = metricValue(goal, p);
  // Objectif nul/négatif (défi malformé) → considéré atteint, jamais d'Infinity/ratio négatif.
  if (!(goal.n > 0)) return { ratio: 1, label: '✓' };
  const ratio = Math.max(0, Math.min(1, v / goal.n));
  return { ratio, label: Math.max(0, Math.min(v, goal.n)) + '/' + goal.n };
}

// Verrouillage SÉQUENTIEL : le 1er palier est toujours ouvert ; chaque suivant exige la
// complétion du précédent (lue dans la méta). Pur/testable.
export function challengeUnlocked(index: number, completed: string[]): boolean {
  if (index <= 0) return true;
  const prev = CHALLENGES[index - 1];
  return !!prev && completed.includes(prev.id);
}

// Le défi `id` est-il jouable au vu des complétions ? (utilitaire d'aiguillage UI)
export function challengeUnlockedById(id: string, completed: string[]): boolean {
  const i = challengeIndex(id);
  return i >= 0 && challengeUnlocked(i, completed);
}

// Nombre de paliers validés (pour un libellé « 3 / 8 » sur l'accueil).
export function challengesCleared(completed: string[]): number {
  return CHALLENGES.filter(c => completed.includes(c.id)).length;
}
